// @ts-strict-ignore
import {RowType} from 'common/query-builder/query-entry-section/row-types/types';
import {MetricEntry} from 'common/query-builder/query-entry-section/types';
import {
  MetricType,
  RETENTION_MATH_TYPES,
  RetentionAlignmentType,
  RetentionType,
  TopLevelMetricType,
} from 'common/types/reports/bookmark';
import {TimeUnit} from 'common/widgets/date-range-picker/models/types';
import {RestrictEntitySaving} from 'common/report/constants';
import {getAllSavedBehaviors} from 'common/query-builder/query-entry-section/row-types/metric-entry/metrics/behavior-utils';
import {BehaviorsResource} from 'common/report/model-resources/behaviors';
import {CurrentProject} from 'common/report/queries/app-api/project-metadata-client';

export function hasSavedRetentionBehaviors({behaviors}: {behaviors: BehaviorsResource}): boolean {
  const savedBehaviors = getAllSavedBehaviors({behaviors, metricType: MetricType.Retention});
  return savedBehaviors.length > 0;
}

export function canSaveForPlan(currentProject: CurrentProject): boolean {
  return Boolean(currentProject?.canSaveEntity(RestrictEntitySaving.BehaviorMetrics));
}

export function getNewEntry({
  firstClassMetricsEnabled,
  isExpanded,
  type,
}: {
  firstClassMetricsEnabled?: boolean;
  isExpanded: boolean;
  type: RowType;
}): MetricEntry {
  const metricEntryType = firstClassMetricsEnabled ? TopLevelMetricType.NewMetricEntry : TopLevelMetricType.MetricEntry;
  return type === MetricType.Retention
    ? {
        type: metricEntryType,
        behavior: {
          type: MetricType.Retention,
          name: null,
          retentionType: `birth` as RetentionType,
          retentionAlignmentType: RetentionAlignmentType.Birth,
          retentionUnit: TimeUnit.Day,
          retentionUnboundedMode: `none`,
          retentionCustomBucketSizes: [],
          behaviors: [
            {
              type: MetricType.Event,
              name: null,
              filters: [],
            },
            {
              type: MetricType.Event,
              name: null,
              filters: [],
            },
          ],
        },
        measurement: {
          aggregationOperator: RETENTION_MATH_TYPES.RETENTION_RATE,
          retentionBuckets: [],
          retentionBucketIndex: 0,
        },
        isExpanded,
      }
    : {
        type: metricEntryType,
        behavior: {
          type: MetricType.RetentionFrequency,
          name: null,
          retentionType: `addiction`,
          retentionAlignmentType: RetentionAlignmentType.Birth,
          retentionUnit: TimeUnit.Day, // TODO
          retentionUnboundedMode: `none`,
          behaviors: [
            {
              type: MetricType.Event,
              name: null,
              filters: [],
            },
          ],
        },
        measurement: {
          aggregationOperator: RETENTION_MATH_TYPES.RETENTION_RATE,
          retentionBuckets: [],
          retentionBucketIndex: 0,
        },
        isExpanded,
      };
}

export function getRetentionCountingMethod(entry: MetricEntry): string {
  if (![MetricType.Retention, MetricType.RetentionFrequency].includes(entry.behavior.type)) {
    throw new Error(`Must be Retention metric.`);
  }
  const {retentionType, retentionUnboundedMode} = entry.behavior;
  const {retentionCumulative} = entry.measurement;

  if (retentionType === `addiction`) {
    if (retentionCumulative) {
      return `cumulative`;
    }
    return `non-cumulative`;
  }

  if (retentionUnboundedMode === `carry_back`) {
    return `unbounded`;
  } else if (retentionUnboundedMode === `carry_forward`) {
    return `unbounded_forward`;
  } else if (retentionUnboundedMode === `consecutive_forward`) {
    return `consecutive_forward`;
  }
  return `bounded`;
}
